import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Chessboard } from 'react-chessboard';
import { db } from '../../firebaseConfig';
import Header from '../Header';
import { useSoundEffects } from '../game/hooks/useSoundEffects';
import '../GamePage.css';
import './Spectator.css';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const SpectatorGame = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!gameId) {
      setError('No game ID provided');
      setLoading(false);
      return;
    }

    const gameRef = doc(db, 'games', gameId);
    const unsubscribe = onSnapshot(
      gameRef,
      (snap) => {
        if (snap.exists()) {
          setGame({ id: snap.id, ...snap.data() });
          setError('');
        } else {
          setError('Game not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Spectator snapshot error:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [gameId]);

  // Play move / game start / game end sounds as state arrives over Firebase
  const sound = useSoundEffects(game);

  if (loading) {
    return (
      <div className="game-container">
        <Header showUserSection={false} />
        <div className="game-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="game-container">
        <Header showUserSection={false} />
        <div className="game-content">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Unable to spectate</h2>
            <p className="error-text">{error || 'Game unavailable'}</p>
            <button onClick={() => navigate('/spectate')} className="back-button">
              Back to spectate entry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const whiteCount = game.whiteTeam?.length || 0;
  const blackCount = game.blackTeam?.length || 0;
  const statusLabel =
    game.status === 'in_progress' ? 'In Progress'
    : game.status === 'waiting' ? 'Waiting for Players'
    : game.status === 'completed' ? 'Completed'
    : game.status || 'Unknown';

  return (
    <div className="game-container">
      <Header showUserSection={false} />

      <div className="game-content-active spectator-content">
        <div className="spectator-header">
          <h2 className="spectator-title">{game.name || 'Living Chess'}</h2>
          <span className={`spectator-status status-${game.status}`}>{statusLabel}</span>
        </div>

        <div className="spectator-layout">
          <div className="chess-board-container spectator-board">
            <div className="turn-indicator">
              <div className="turn-info">
                Current Turn:{' '}
                <span className={`turn-${game.currentTurn}`}>
                  {game.currentTurn === 'white' ? 'White' : 'Black'}
                </span>
              </div>
              <button
                type="button"
                className="sound-toggle-button"
                onClick={sound.toggleSound}
                aria-label={sound.muted ? 'Unmute sound effects' : 'Mute sound effects'}
                title={sound.muted ? 'Unmute sound effects' : 'Mute sound effects'}
              >
                {sound.muted ? '🔇' : '🔊'}
              </button>
            </div>

            <div className="chessboard-wrapper">
              <Chessboard
                options={{
                  position: game.fen || STARTING_FEN,
                  boardOrientation: 'white',
                  arePiecesDraggable: false,
                  customBoardStyle: {
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                  },
                  customDarkSquareStyle: { backgroundColor: '#4a5568' },
                  customLightSquareStyle: { backgroundColor: '#e2e8f0' },
                  id: 'spectator-board'
                }}
              />
            </div>

            {game.status === 'completed' && (
              <div className="game-over-banner">
                <h3>Game Over!</h3>
                {game.endReason && <p>{game.endReason}</p>}
                {game.winner && (
                  <p className="winner-text">
                    Winner: {game.winner === 'white' ? 'White Team' : 'Black Team'}
                  </p>
                )}
              </div>
            )}
          </div>

          <aside className="spectator-info">
            <div className="spectator-info-card">
              <h4>♔ White Team</h4>
              <p className="spectator-team-count">{whiteCount} {whiteCount === 1 ? 'player' : 'players'}</p>
            </div>
            <div className="spectator-info-card">
              <h4>♚ Black Team</h4>
              <p className="spectator-team-count">{blackCount} {blackCount === 1 ? 'player' : 'players'}</p>
            </div>
            <div className="spectator-info-card spectator-note">
              You're spectating — chat and voting are hidden.
            </div>
          </aside>
        </div>

        <button onClick={() => navigate('/spectate')} className="back-button-game">
          ← Watch a different game
        </button>
      </div>
    </div>
  );
};

export default SpectatorGame;
